import React, { useState } from 'react'
import { Input, Button, Form } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import intl from 'react-intl-universal'
import SvgIcon from '@/components/svgIcon'
import VerifyCodeInput from '../verifyCodeInput'
import { useSearchParams, useNavigate } from 'react-router-dom'
import './index.scss'
import { useLoginStore } from '@/store/loginStore'
import { weChatLoginBind, dingtalkLoginBind, weComBind, feishuLoginBind } from '@/services/login'
import { CLIENT_TYPE, Action, THIRD_PARTY, authType } from '@/config/default'
import { getSMSVerificationCodeApi } from '@/services/common'
import { LOGIN_PATH } from '@/routers/login'
import { loginSuccess } from '@/utils/utils'
import type { Action as ActionType } from '@/services/common/types'

const PHONE_REGEX = /^1[3-9]\d{9}$/
const VERIFY_CODE_REGEX = /^\d+$/

function BindAccount() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const thirdPartyState = searchParams.get('state')
  const platform = thirdPartyState?.split('@')?.[0] || ''
  const enterpriseCodeUrl = thirdPartyState?.split('@')?.[1] || ''
  const isWechat = thirdPartyState?.indexOf(THIRD_PARTY.WECHAT) !== -1

  const { setShowBindAccount, thirdInfo } = useLoginStore()

  const [form] = Form.useForm()
  const enterpriseCode = Form.useWatch('enterpriseCode')
  const [mobileValue, setMobileValue] = useState('')
  const { validateFields, getFieldsValue } = form

  const [loading, setLoading] = useState(false)

  const isPhoneValid = mobileValue && PHONE_REGEX.test(mobileValue)

  // 发送验证码
  const handleSendVerifyCode = async () => {
    const enterpriseCode = getFieldsValue().enterpriseCode
    let action: ActionType = Action.BIND_GERENWEIXIN
    if (platform === THIRD_PARTY.WECHAT) {
      // 个人微信绑定
      action = Action.BIND_GERENWEIXIN
    } else if (platform === THIRD_PARTY.DINGTALK) {
      // 钉钉绑定
      action = Action.BIND_DING_TALK
    } else if (platform === THIRD_PARTY.WECOM) {
      // 企业微信绑定
      action = Action.BIND_QIYEWEIXIN
    } else if (platform === THIRD_PARTY.LARK) {
      // 飞书绑定
      action = Action.BIND_FEISHU
    }
    getSMSVerificationCodeApi({
      enterpriseCode,
      mobile: mobileValue,
      action,
      clientId: 'webapp'
    })
  }


  const handleSubmit = async () => {
    try {
      setLoading(true)
      const fieldsValue = await validateFields()
      if (thirdInfo?.type === THIRD_PARTY.WECHAT) {
        // 个人微信绑定
        const res = await weChatLoginBind({
          clientType: CLIENT_TYPE.PC,
          enterpriseCode: fieldsValue.enterpriseCode,
          mobile: fieldsValue.mobile,
          smsCaptcha: fieldsValue.verifyCode,
          openId: thirdInfo.openId,
          unionId: thirdInfo.unionId
        })
        if (res?.status) {
          loginSuccess(res.loginResponse)
        }
      } else if (thirdInfo?.type === THIRD_PARTY.DINGTALK) {
        // 钉钉绑定
        const res = await dingtalkLoginBind({
          clientType: CLIENT_TYPE.PC,
          enterpriseCode: fieldsValue.enterpriseCode,
          mobile: fieldsValue.mobile,
          smsCaptcha: fieldsValue.verifyCode,
          dingTalkUserId: thirdInfo.dingTalkUserId,
          authType: thirdInfo.authType,
        })
        if (res?.status) {
          loginSuccess(res.loginResponse)
        }
      } else if (thirdInfo?.type === THIRD_PARTY.WECOM) {
        // 企业微信绑定
        const res = await weComBind({
          clientType: CLIENT_TYPE.PC,
          enterpriseCode: fieldsValue.enterpriseCode,
          mobile: fieldsValue.mobile,
          smsCaptcha: fieldsValue.verifyCode,
          qwUserId: thirdInfo.qwUserId,
          authType: thirdInfo.authType,
        })
        if (res?.status) {
          loginSuccess(res.loginResponse)
        }
      } else if (thirdInfo?.type === THIRD_PARTY.LARK) {
        // 飞书绑定
        const res = await feishuLoginBind({
          clientType: CLIENT_TYPE.PC,
          enterpriseCode: fieldsValue.enterpriseCode,
          mobile: fieldsValue.mobile,
          smsCaptcha: fieldsValue.verifyCode,
          feishuUserId: thirdInfo.feishuUserId,
          feishuUserName: thirdInfo.feishuUserName,
          authType: thirdInfo.authType,
        })
        if (res?.status) {
          loginSuccess(res.loginResponse)
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const onBack = () => {
    setSearchParams({})
    navigate(LOGIN_PATH)
    setShowBindAccount(false)
  }

  return (
    <div className='bindAccount-wrapper'>
      <div className='bindAccount-title'>
        <ArrowLeftOutlined onClick={onBack} />
        {intl.get('login.bindAccount')}
      </div>
      <div className='bindAccount-subtitle'>{intl.get('login.pleaseBindEnterpriseAccount')}</div>
      <Form className='bindAccount-form' form={form}>
        <div className='loginForm-input-wrap'>
          <div className='loginForm-input-text'>{intl.get('login.enterpriseCode')}</div>
          <Form.Item
            name="enterpriseCode"
            rules={[{ required: true, message: intl.get('login.pleaseEnterEnterpriseCode') }]}
            initialValue={enterpriseCodeUrl}
          >
            <Input
              className='loginForm-input'
              placeholder={intl.get('login.pleaseEnterEnterpriseCode')}
              prefix={<SvgIcon name='logoEnterprise' />}
              disabled={!isWechat}
            />
          </Form.Item>
        </div>
        <div className='loginForm-input-wrap'>
          <div className='loginForm-input-text'>{intl.get('login.phoneNumber')}</div>
          <Form.Item
            name="mobile"
            rules={[
              { required: true, message: intl.get('login.pleaseEnterPhoneNumber') },
              { pattern: PHONE_REGEX, message: intl.get('login.pleaseEnterCorrectPhoneNumber') }
            ]}
          >
            <Input
              className='loginForm-input'
              placeholder={intl.get('login.pleaseEnterPhoneNumber')}
              prefix={<SvgIcon name='tel' />}
              onChange={(e) => setMobileValue(e.target.value.trim())}
            />
          </Form.Item>
        </div>
        <div className='loginForm-input-wrap'>
          <div className='loginForm-input-text'>{intl.get('login.verifyCodeLabel')}</div>
          <Form.Item
            name="verifyCode"
            rules={[
              { required: true, message: intl.get('login.pleaseEnterVerifyCode') },
              { pattern: VERIFY_CODE_REGEX, message: intl.get('login.pleaseEnterCorrectVerifyCodeFormat') }
            ]}
            getValueFromEvent={(value) => value}
          >
            <VerifyCodeInput
              disabled={!(isPhoneValid || enterpriseCode)}
              onSend={handleSendVerifyCode}
              inputClassName='loginForm-input'
            />
          </Form.Item>
        </div>
      </Form>
      <Button
        className='bindAccount-btn'
        type='primary'
        loading={loading}
        onClick={handleSubmit}
      >
        {intl.get('login.confirmBind')}
      </Button>
    </div>
  )
}

export default BindAccount